import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  ButtonState,
  Padding,
  Margin,
  FlexType,
  FontWeight,
  IconType,
  Width,
  AdvancedSettings,
  HoverStyle,
  ButtonPosition,
} from '../types/fitQuizButton/ButtonState';
import { ShopFeature } from '../types/fitQuizButton/ShopFeature';
import { hangerIcons } from '../utils/hanger.icons';
import { shoeIcons } from '../utils/shoe.icons';
import { specialIcons } from '../utils/special.icons';
import client from '../clients/client';
import { State } from '../redux/redux.state';
import { Icon } from '../types/icons';
import { mapCssToButtonState } from '../utils/map.css.to.button.state';
import { swimwearIcons } from '../utils/swimwear.icons';
import { bottomsIcons } from '../utils/bottoms.icons';
import { jacketIcons } from '../utils/jacket.icons';
import { apparelIcons } from '../utils/apparel.icons';

type ResponseState = State<ButtonState> & {
  setStatus: 'idle' | 'loading' | 'successful' | 'error';
  loadingMessage?: string;
};

const defaultAdvancedSettings: AdvancedSettings = {
  fontSize: 16,
  backgroundColor: 'transparent',
  fontWeight: '400',
  borderType: 'none',
  borderShape: 'solid',
  borderColor: '#000000',
  borderRadius: 0,
  underline: false,
  hoverStyle: 'None',
  buttonPosition: 'Default',
  padding: { horizontal: 0, vertical: 0 },
  margin: { horizontal: 0, top: 10, bottom: 10 },
  width: 'auto',
  justifyContent: 'flex-start',
  onlyCapitalLetters: false,
}

export const initialState: ResponseState = {
  data: {
    fontColor: '#000000',
    desktopAndButtonStyleSame: true,
    desktopAdvancedSettings: defaultAdvancedSettings,
    mobileAdvancedSettings: defaultAdvancedSettings,
    iconType: 'Special',
    specialIcon: specialIcons[0],
    swimwearIcon: swimwearIcons[0],
    hangerIcon: hangerIcons[0],
    shoeIcon: shoeIcons[0],
    bottomsIcon: bottomsIcons[0],
    jacketIcon: jacketIcons[0],
    apparelIcon: apparelIcons[0]
  },
  status: 'idle',
  setStatus: 'idle',
};

export const setButtonCss = createAsyncThunk<ShopFeature, ShopFeature>(
  '/features/updateCssFeature',
  async (feature: ShopFeature, { rejectWithValue }) => {
    try {
      let id = feature.id ?? 0;
      const { data } = await client.post(`/features/css/`, { ...feature, id });
      return data;
    } catch (error: any) {
      const { status, data } = error.response;
      return rejectWithValue({ status, errorMessage: data });
    }
  }
);

export const getButtonCss = createAsyncThunk<ShopFeature | {}, string>(
  'features/getCssFeatures',
  async (shopId: string, { rejectWithValue }) => {
    try {
      const { data } = await client.get(`/features/css/${shopId}`);
      return data;
    } catch (error: any) {
      const { status, data } = error.response;
      return rejectWithValue({ status, errorMessage: data });
    }
  }
);

export const buttonSlice = createSlice({
  name: 'fitQuizButton',
  initialState,
  reducers: {
    changeFontColor: (state, action: PayloadAction<string>) => {
      state.data.fontColor = action.payload;
    },
    changeIconType: (state, action: PayloadAction<IconType>) => {
      state.data.iconType = action.payload;
    },
    changeHangerIcon: (state, action: PayloadAction<Icon>) => {
      state.data.hangerIcon = action.payload;
    },
    changeShoeIcon: (state, action: PayloadAction<Icon>) => {
      state.data.shoeIcon = action.payload;
    },
    changeSpecialIcon: (state, action: PayloadAction<Icon>) => {
      state.data.specialIcon = action.payload;
    },
    changeBottomIcon: (state, action: PayloadAction<Icon>) => {
      state.data.bottomsIcon = action.payload;
    },
    changeSwimwearIcon: (state, action: PayloadAction<Icon>) => {
      state.data.swimwearIcon = action.payload;
    },
    changeApparelIcon: (state, action: PayloadAction<Icon>) => {
      state.data.apparelIcon = action.payload;
    },
    changeJacketIcon: (state, action: PayloadAction<Icon>) => {
      state.data.jacketIcon = action.payload;
    },
    setCustomIconUrl: (state, action: PayloadAction<string>) => {
      state.data.customIconUrl = action.payload;
    },
    changeDesktopFontSize: (state, action: PayloadAction<number>) => {
      state.data.desktopAdvancedSettings.fontSize = action.payload;
    },
    changeDesktopFontWeight: (state, action: PayloadAction<FontWeight>) => {
      state.data.desktopAdvancedSettings.fontWeight = action.payload;
    },

    changeDesktopUnderlineStatus: (state, action: PayloadAction<boolean>) => {
      state.data.desktopAdvancedSettings.underline = action.payload;
    },
    changeDesktopOnlyCapitalLettersStatus: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.data.desktopAdvancedSettings.onlyCapitalLetters = action.payload;
    },
    changeDesktopBorderColor: (state, action: PayloadAction<string>) => {
      state.data.desktopAdvancedSettings.borderColor = action.payload;
    },
    changeDesktopBackgroundColor: (state, action: PayloadAction<string>) => {
      state.data.desktopAdvancedSettings.backgroundColor = action.payload;
    },
    changeDesktopBorderType: (state, action: PayloadAction<any>) => {
      state.data.desktopAdvancedSettings.borderType = action.payload;
    },
    changeDesktopBorderShape: (state, action: PayloadAction<any>) => {
      state.data.desktopAdvancedSettings.borderShape = action.payload;
    },
    changeDesktopBorderRadius: (state, action: PayloadAction<number>) => {
      state.data.desktopAdvancedSettings.borderRadius = action.payload;
    },
    changeDesktopPadding: (state, action: PayloadAction<Padding>) => {
      state.data.desktopAdvancedSettings.padding = action.payload;
    },
    changeDesktopMargin: (state, action: PayloadAction<Margin>) => {
      state.data.desktopAdvancedSettings.margin = action.payload;
    },
    changeDesktopWidth: (state, action: PayloadAction<Width>) => {
      state.data.desktopAdvancedSettings.width = action.payload;
    },
    changeDesktopJustifyContent: (state, action: PayloadAction<FlexType>) => {
      state.data.desktopAdvancedSettings.justifyContent = action.payload;
    },    
    changeDesktopHoverStyle: (state, action: PayloadAction<HoverStyle>) => {
      state.data.desktopAdvancedSettings.hoverStyle = action.payload
    },
    changeDesktopHoverBackgroundColor: (state, action: PayloadAction<string>) => {
      state.data.desktopAdvancedSettings.hoverBackgroundColor = action.payload
    },
    changeDesktopButtonPosition: (state, action: PayloadAction<ButtonPosition>) => {
      state.data.desktopAdvancedSettings.buttonPosition = action.payload
    },
    changeMobileFontSize: (state, action: PayloadAction<number>) => {
      state.data.mobileAdvancedSettings.fontSize = action.payload;
    },
    changeMobileFontWeight: (state, action: PayloadAction<FontWeight>) => {
      state.data.mobileAdvancedSettings.fontWeight = action.payload;
    },
    changeMobileUnderlineStatus: (state, action: PayloadAction<boolean>) => {
      state.data.mobileAdvancedSettings.underline = action.payload;
    },
    changeMobileOnlyCapitalLettersStatus: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.data.mobileAdvancedSettings.onlyCapitalLetters = action.payload;
    },
    changeMobileBorderColor: (state, action: PayloadAction<string>) => {
      state.data.mobileAdvancedSettings.borderColor = action.payload;
    },
    changeMobileBackgroundColor: (state, action: PayloadAction<string>) => {
      state.data.mobileAdvancedSettings.backgroundColor = action.payload;
    },
    changeMobileBorderType: (state, action: PayloadAction<any>) => {
      state.data.mobileAdvancedSettings.borderType = action.payload;
    },
    changeMobileBorderRadius: (state, action: PayloadAction<number>) => {
      state.data.mobileAdvancedSettings.borderRadius = action.payload;
    },
    changeMobileBorderShape: (state, action: PayloadAction<any>) => {
      state.data.mobileAdvancedSettings.borderShape = action.payload;
    },
    changeMobilePadding: (state, action: PayloadAction<Padding>) => {
      state.data.mobileAdvancedSettings.padding = action.payload;
    },
    changeMobileMargin: (state, action: PayloadAction<Margin>) => {
      state.data.mobileAdvancedSettings.margin = action.payload;
    },
    changeMobileWidth: (state, action: PayloadAction<Width>) => {
      state.data.mobileAdvancedSettings.width = action.payload;
    },
    changeMobileJustifyContent: (state, action: PayloadAction<FlexType>) => {
      state.data.mobileAdvancedSettings.justifyContent = action.payload;
    },
    changeMobileHoverStyle: (state, action: PayloadAction<HoverStyle>) => {
      state.data.mobileAdvancedSettings.hoverStyle = action.payload
    },
    changeMobileHoverBackgroundColor: (state, action: PayloadAction<string>) => {
      state.data.mobileAdvancedSettings.hoverBackgroundColor = action.payload
    },
    changeMobileButtonPosition: (state, action: PayloadAction<ButtonPosition>) => {
      state.data.mobileAdvancedSettings.buttonPosition = action.payload
    },
    resetData: (state) => {
      state.data = initialState.data;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setButtonCss.pending, (state) => {
        state.status = 'loading';
        state.loadingMessage = 'Plugging in your new button settings...';
      })
      .addCase(setButtonCss.fulfilled, (state, action) => {
        state.status = 'successful';
      })
      .addCase(setButtonCss.rejected, (state, action) => {
        state.status = 'error';
        state.errors = action.payload;
      })
      .addCase(getButtonCss.pending, (state) => {
        state.status = 'loading';
        state.loadingMessage = 'Fetching your button settings...';
      })
      .addCase(getButtonCss.fulfilled, (state, action) => {
        state.status = 'successful';
        if (Object.keys(action.payload).length === 0) {
          let buttonState = mapCssToButtonState('');
          state.data = buttonState;
        } else {
          let featureValue = action.payload as ShopFeature;
          state.data = mapCssToButtonState(featureValue.value);
        }
      })
      .addCase(getButtonCss.rejected, (state, action) => {
        state.status = 'error';
        state.errors = action.payload;
      });
  },
});

export const {
  resetData,
  changeIconType,
  changeFontColor,
  changeHangerIcon,
  changeShoeIcon,
  changeSpecialIcon,
  changeBottomIcon,
  changeJacketIcon,
  changeSwimwearIcon,
  changeApparelIcon,
  setCustomIconUrl,
  changeDesktopFontSize,
  changeDesktopFontWeight,
  changeDesktopUnderlineStatus,
  changeDesktopOnlyCapitalLettersStatus,
  changeDesktopBorderType,
  changeDesktopBorderShape,
  changeDesktopBackgroundColor,
  changeDesktopBorderColor,
  changeDesktopBorderRadius,
  changeDesktopMargin,
  changeDesktopWidth,
  changeDesktopPadding,
  changeDesktopJustifyContent,
  changeDesktopHoverBackgroundColor,
  changeDesktopHoverStyle,
  changeDesktopButtonPosition,
  changeMobileFontSize,
  changeMobileFontWeight,
  changeMobileUnderlineStatus,
  changeMobileOnlyCapitalLettersStatus,
  changeMobileBorderType,
  changeMobileBorderShape,
  changeMobileBackgroundColor,
  changeMobileBorderColor,
  changeMobileBorderRadius,
  changeMobileMargin,
  changeMobileWidth,
  changeMobilePadding,
  changeMobileJustifyContent,
  changeMobileHoverBackgroundColor,
  changeMobileHoverStyle,
  changeMobileButtonPosition,
} = buttonSlice.actions;

export default buttonSlice.reducer;
